import { useState, useRef, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link2, ChevronDown, Palette, RemoveFormatting,
} from 'lucide-react'
import {
  TEXT_COLORS, TEXT_SIZES, asHexOrDefault, currentColor, currentSizeLabel,
} from './rich-text-styling'
import { promptLink } from './rich-text-link'
import { richTextExtensions } from './rich-text-extensions'

const HEADING_OPTIONS = [
  { label: 'Normal', value: 0 },
  { label: 'H2 — Heading', value: 2 },
  { label: 'H3 — Subheading', value: 3 },
  { label: 'H4 — Heading 4', value: 4 },
]

/**
 * Normalize the value the field carries into `{rawHtml, currentFormat}`.
 *
 *  - Legacy string                 → { rawHtml: <string>, currentFormat: null }
 *  - { format, value, processed? } → { rawHtml: value, currentFormat: format }
 */
const normalizeIncomingValue = (value, formats, defaultFormat) => {
  if (value && typeof value === 'object' && 'value' in value) {
    return {
      rawHtml: value.value ?? '',
      currentFormat: value.format ?? defaultFormat ?? (formats?.[0] ?? null),
    }
  }
  return {
    rawHtml: typeof value === 'string' ? value : '',
    currentFormat: defaultFormat ?? (formats?.[0] ?? null),
  }
}

/**
 * `embedded` — l'éditeur vit DANS un formulaire, pas dans une pop-in : il rend
 * sa barre d'outils et sa surface de saisie, sans pied « Annuler / Appliquer »
 * ni prise de focus au montage. Il remonte alors la valeur à CHAQUE frappe
 * (`onSave` joue le rôle d'un `onChange`), puisqu'il n'y a plus de bouton pour
 * valider — c'est le « Enregistrer » du formulaire qui tranche, comme pour un
 * champ texte voisin.
 */
export const RichTextEditor = ({ value, label, formats, defaultFormat, onSave, onClose, embedded = false }) => {
  const hasFormats = Array.isArray(formats) && formats.length > 0
  const { rawHtml, currentFormat } = normalizeIncomingValue(value, formats, defaultFormat)
  const [format, setFormat] = useState(currentFormat)

  const editor = useEditor({
    extensions: richTextExtensions(),
    content: rawHtml,
    // Intégré à un formulaire, l'éditeur ne vole pas le focus au montage : la
    // page en compte plusieurs, et le premier rendu sauterait au corps.
    autofocus: !embedded,
    onUpdate: embedded ? ({ editor: ed }) => emitRef.current(ed) : undefined,
  })

  const emit = (ed) => {
    if (!ed) return
    const html = ed.getHTML()
    // Emit the new {format, value} shape only when the def declared formats.
    // Otherwise stay in legacy string mode for backward compatibility.
    if (hasFormats) {
      onSave({ format: format ?? formats[0], value: html })
    } else {
      onSave(html)
    }
  }

  // `onUpdate` est figé à la création de l'éditeur : sans cette référence, il
  // rappellerait éternellement le `onSave` (et le `format`) du premier rendu.
  const emitRef = useRef(emit)
  emitRef.current = emit

  // `content:` n'est lu QU'À la création de l'éditeur. Dans un formulaire, la
  // valeur arrive après — le contenu est chargé par le réseau alors que le
  // champ est déjà monté : sans cette synchronisation, l'éditeur reste
  // désespérément vide et le premier enregistrement effacerait le texte.
  //
  // On ne réinjecte QUE si le HTML diffère de ce que l'éditeur tient déjà :
  // autrement chaque frappe (qui remonte la valeur, donc redescend ici)
  // reposerait le document et renverrait le curseur au début.
  useEffect(() => {
    if (!editor || !embedded) return
    if (rawHtml !== editor.getHTML()) {
      editor.commands.setContent(rawHtml || '', { emitUpdate: false })
    }
  }, [editor, embedded, rawHtml])

  const handleSave = () => emit(editor)

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && onClose) onClose()
  }

  if (!editor) return null

  return (
    <div className="flex flex-col gap-3" onKeyDown={handleKeyDown}>
      <div className="border border-gray-300 rounded-lg overflow-hidden">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5">

          {/* Heading selector */}
          <HeadingDropdown editor={editor} />

          <Sep />

          {/* Format */}
          <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold">
            <Bold size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic">
            <Italic size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} label="Underline">
            <UnderlineIcon size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} label="Strikethrough">
            <Strikethrough size={14} />
          </Btn>

          <Sep />

          {/* Taille et couleur du texte */}
          <SizeDropdown editor={editor} />
          <ColorDropdown editor={editor} />

          <Sep />

          {/* Lists */}
          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bullet list">
            <List size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Ordered list">
            <ListOrdered size={14} />
          </Btn>

          <Sep />

          {/* Alignment */}
          <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} label="Align left">
            <AlignLeft size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} label="Center">
            <AlignCenter size={14} />
          </Btn>
          <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} label="Align right">
            <AlignRight size={14} />
          </Btn>

          <Sep />

          {/* Link */}
          <Btn
            onClick={() => promptLink(editor)}
            active={editor.isActive('link')}
            label="Insert a link"
          >
            <Link2 size={14} />
          </Btn>
        </div>

        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none px-3 py-2 min-h-[100px] focus:outline-none"
          aria-label={label}
        />
      </div>

      {/* Intégré au formulaire, l'éditeur n'a pas de pied : rien à valider
          (la frappe remonte déjà), et rien à annuler — c'est « Enregistrer »
          qui décide, pour ce champ comme pour ses voisins. Le sélecteur de
          format, lui, reste : il porte une donnée, pas une commande. */}
      {(!embedded || (hasFormats && formats.length > 1)) && (
        <div className="flex items-center justify-between gap-2">
          {hasFormats && formats.length > 1 ? (
            <label className="flex items-center gap-1.5 text-xs text-gray-500">
              Format
              <select
                value={format ?? formats[0]}
                onChange={(e) => setFormat(e.target.value)}
                className="text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-teal-500"
              >
                {formats.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
          ) : <span />}
          {!embedded && (
            <div className="flex gap-2">
              <button onClick={onClose} className="btn btn-secondary">
                Annuler
              </button>
              <button onClick={handleSave} className="btn btn-primary">
                Appliquer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Sous-composants partagés ─────────────────────────────── */

const Sep = () => <div className="w-px h-4 bg-gray-200 mx-1" />

/**
 * Menu déroulant de la barre d'outils. `children` reçoit `close` : l'item
 * décide quand refermer — le sélecteur de couleur libre doit rester monté
 * tant que la palette native de l'OS est ouverte.
 */
const ToolbarDropdown = ({ label, trigger, isActive, children }) => {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title={label}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        className={`flex items-center gap-1 px-2 py-1.5 rounded text-sm transition-colors ${
          isActive || open ? 'bg-teal-100 text-teal-800' : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        {trigger}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[160px] py-1">
            {children(() => setOpen(false))}
          </div>
        </>
      )}
    </div>
  )
}

const DropItem = ({ onClick, active, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-2 text-left px-3 py-1.5 text-sm transition-colors hover:bg-teal-50 hover:text-teal-700 ${
      active ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'
    }`}
  >
    {children}
  </button>
)

const Swatch = ({ color }) => (
  <span
    className="inline-block w-3.5 h-3.5 rounded-sm shrink-0 border border-gray-200"
    style={{ background: color }}
    aria-hidden="true"
  />
)

/** Taille du texte — en multiples de la taille héritée du bloc. */
const SizeDropdown = ({ editor }) => {
  const current = editor.getAttributes('textStyle').fontSize || null

  return (
    <ToolbarDropdown
      label="Taille du texte"
      isActive={!!current}
      trigger={<><span className="text-xs font-medium">{currentSizeLabel(editor)}</span><ChevronDown size={11} aria-hidden="true" /></>}
    >
      {(close) => (
        <>
          <DropItem active={!current} onClick={() => { editor.chain().focus().unsetFontSize().run(); close() }}>
            <RemoveFormatting size={13} aria-hidden="true" />
            Taille du bloc
          </DropItem>
          <div className="my-1 border-t border-gray-100" />
          {TEXT_SIZES.map(size => (
            <DropItem
              key={size.value}
              active={current === size.value}
              onClick={() => { editor.chain().focus().setFontSize(size.value).run(); close() }}
            >
              <span className="w-8 shrink-0 text-center leading-none" style={{ fontSize: size.value }} aria-hidden="true">Aa</span>
              {size.label}
            </DropItem>
          ))}
        </>
      )}
    </ToolbarDropdown>
  )
}

/** Couleur du texte — jetons du design system, plus une couleur libre. */
const ColorDropdown = ({ editor }) => {
  const current = currentColor(editor)

  return (
    <ToolbarDropdown
      label="Couleur du texte"
      isActive={!!current}
      trigger={<><Palette size={14} aria-hidden="true" /><ChevronDown size={11} aria-hidden="true" /></>}
    >
      {(close) => (
        <>
          <DropItem active={!current} onClick={() => { editor.chain().focus().unsetColor().run(); close() }}>
            <RemoveFormatting size={13} aria-hidden="true" />
            Couleur du bloc
          </DropItem>
          <div className="my-1 border-t border-gray-100" />
          {TEXT_COLORS.map(color => (
            <DropItem
              key={color.value}
              active={current === color.value}
              onClick={() => { editor.chain().focus().setColor(color.value).run(); close() }}
            >
              <Swatch color={color.value} />
              {color.label}
            </DropItem>
          ))}
          <div className="my-1 border-t border-gray-100" />
          <label className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 cursor-pointer hover:bg-teal-50 hover:text-teal-700">
            <Swatch color={current || 'transparent'} />
            Personnalisée
            <input
              type="color"
              className="ml-auto w-6 h-6 p-0 border-0 bg-transparent cursor-pointer"
              value={asHexOrDefault(current)}
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              aria-label="Choisir une couleur personnalisée"
            />
          </label>
        </>
      )}
    </ToolbarDropdown>
  )
}

const Btn = ({ children, onClick, active, label }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`p-1.5 rounded text-sm transition-colors ${
      active ? 'bg-teal-100 text-teal-800' : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    {children}
  </button>
)

const HeadingDropdown = ({ editor }) => {
  const [open, setOpen] = useState(false)

  const getLabel = () => {
    for (const { value } of HEADING_OPTIONS.filter(o => o.value > 0)) {
      if (editor.isActive('heading', { level: value })) return `H${value}`
    }
    return 'Normal'
  }

  const apply = (value) => {
    if (value === 0) {
      editor.chain().focus().setParagraph().run()
    } else {
      editor.chain().focus().setHeading({ level: value }).run()
    }
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors min-w-[64px]"
      >
        {getLabel()} <ChevronDown size={11} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[148px]">
            {HEADING_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => apply(opt.value)}
                className={`w-full text-left px-3 py-1.5 text-sm transition-colors hover:bg-teal-50 hover:text-teal-700 ${
                  (opt.value === 0 && !HEADING_OPTIONS.filter(o => o.value > 0).some(o => editor.isActive('heading', { level: o.value })))
                  || (opt.value > 0 && editor.isActive('heading', { level: opt.value }))
                    ? 'bg-teal-50 text-teal-700 font-medium'
                    : 'text-gray-700'
                }`}
                style={opt.value > 0 ? { fontSize: `${1.1 - (opt.value - 2) * 0.1}em`, fontWeight: 600 } : {}}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * La cellule de formulaire du type `rich-text` : le texte s'écrit LÀ, mis en
 * forme, comme le chapô juste au-dessus.
 *
 * Elle vit dans ce fichier et non dans `cells.jsx`, dont le contrat est
 * « aucun état interne, aucun chargement » — monter un éditeur tiptap y
 * dérogerait. Un bouton d'aperçu ouvrant une pop-in aurait mis le corps de
 * l'article, le champ le plus long du formulaire, derrière un clic.
 */
export const RichTextCell = ({ id, value, onChange, formats, defaultFormat, label }) => (
  <div id={id}>
    <RichTextEditor
      value={value}
      label={label}
      formats={formats}
      defaultFormat={defaultFormat}
      onSave={onChange}
      embedded
    />
  </div>
)
